// 사건 데이터에서 캘린더 이벤트 자동 추출.
// read-only — 사건 데이터 바뀌면 자동 반영. 사용자가 캘린더에서 직접 편집 불가.
// (편집은 사건 상세에서)
//
// 5종: 감정인 선정일 / 감정 착수일 / 예상감정료 제출일 / 감정서 제출일 / 진행단계 마감

import type { Appraisal } from 'firebase/appraisals-service';

export type EventKind =
  | 'asign' // 감정인 선정일 (asign_date)
  | 'assignment' // 감정 착수일 (assignment_date = 신문일/촉탁일)
  | 'submission' // 예상감정료 제출일 (submission_date)
  | 'final_submission' // 감정서 제출일 (appraisal_submission_date)
  | 'progress_due'; // 진행단계 마감 (appraisal_progress[].end_date)

export interface DerivedEvent {
  id: string; // 고유 id (appraisalId + kind + [index] 조합)
  appraisalId: string;
  kind: EventKind;
  title: string; // 캘린더 표시용 (라벨 prefix + 사건명)
  date: string; // 'YYYY-MM-DD'
  stageName?: string; // progress_due 일 때 단계명 (자료요청/현장조사 등)
}

export interface KindColors {
  asign: string;
  assignment: string;
  submission: string;
  final_submission: string;
  progress_due: string;
}

export const KIND_LABELS: Record<EventKind, string> = {
  asign: '선정',
  assignment: '착수',
  submission: '예상감정료',
  final_submission: '감정서 제출',
  progress_due: '진행단계'
};

/**
 * 사건 목록을 캘린더 이벤트 목록으로 변환.
 * 한 사건이 여러 이벤트를 만들 수 있음 (각 날짜 필드 + 진행단계 row 마다).
 */
export function deriveEventsFromAppraisals(rows: Appraisal[]): DerivedEvent[] {
  const out: DerivedEvent[] = [];
  for (const r of rows) {
    const title = r.title || '(제목 없음)';

    if (r.asign_date) {
      out.push({
        id: `${r.id}::asign`,
        appraisalId: r.id,
        kind: 'asign',
        title: `[선정] ${title}`,
        date: r.asign_date
      });
    }
    if (r.assignment_date) {
      out.push({
        id: `${r.id}::assignment`,
        appraisalId: r.id,
        kind: 'assignment',
        title: `[착수] ${title}`,
        date: r.assignment_date
      });
    }
    if (r.submission_date) {
      out.push({
        id: `${r.id}::submission`,
        appraisalId: r.id,
        kind: 'submission',
        title: `[예상감정료] ${title}`,
        date: r.submission_date
      });
    }
    if (r.appraisal_submission_date) {
      out.push({
        id: `${r.id}::final_submission`,
        appraisalId: r.id,
        kind: 'final_submission',
        title: `[감정서 제출] ${title}`,
        date: r.appraisal_submission_date
      });
    }
    const progressRows = r.appraisal_progress ?? [];
    progressRows.forEach((p, idx) => {
      if (p.end_date) {
        out.push({
          id: `${r.id}::progress::${p.index ?? idx}`,
          appraisalId: r.id,
          kind: 'progress_due',
          title: `[${p.type}] ${title}`,
          date: p.end_date,
          stageName: p.type
        });
      }
    });
  }
  return out;
}

/**
 * EventKind 별 색상 매핑. theme.palette 에서 받아 호출.
 * 13-calendar-design.md §3.3 이벤트 색상 룰.
 */
export function buildKindColors(palette: {
  primary: { main: string };
  success: { main: string };
  info: { main: string };
  secondary: { main: string };
  error: { main: string };
}): KindColors {
  return {
    asign: palette.success.main, // 🟢 선정
    assignment: palette.primary.main, // 🔵 착수
    submission: palette.info.main, // 🟦 예상감정료
    final_submission: palette.secondary.main, // 🟣 감정서
    progress_due: palette.error.main // 🔴 진행단계 마감
  };
}

/**
 * 오늘 날짜에 마감되는 이벤트 카운트 (메뉴 옆 badge 등에 사용).
 */
export function countTodayDeadlines(events: DerivedEvent[], now: Date = new Date()): number {
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return events.filter((e) => e.date === today && e.kind === 'progress_due').length;
}

/**
 * 이번 주 (월~일) 마감 이벤트 카운트 — 사이드바 요약용.
 */
export function countThisWeekDeadlines(events: DerivedEvent[], now: Date = new Date()): number {
  const day = now.getDay(); // 0=일, 1=월, ..., 6=토
  // 이번 주 월요일
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((day + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  return events.filter((e) => {
    if (e.kind !== 'progress_due') return false;
    const d = new Date(e.date);
    return d >= monday && d <= sunday;
  }).length;
}
